'use client'

import React from 'react'
import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

export function Card3d({
  children,
  containerClassName,
  glareClassName,
  cardClassName,
  glare = true,
  rotationIntensity = 20,
  scale = 1.05,
  perspective = 1000,
  disabled = false,
  ...props
}) {
  const containerRef = useRef(null)
  const cardRef = useRef(null)
  const glareRef = useRef(null)
  const [rotation, setRotation] = useState({ x: 0, y: 0 })
  const [glarePosition, setGlarePosition] = useState({ x: 0, y: 0 })
  const [isHovered, setIsHovered] = useState(false)

  const handleMouseMove = (e) => {
    if (disabled || !containerRef.current || !cardRef.current) return

    const container = containerRef.current
    const rect = container.getBoundingClientRect()

    // Calculate cursor position relative to card center (normalized from -1 to 1)
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5

    // Calculate rotation (inverted for natural feel)
    const rotateY = x * rotationIntensity
    const rotateX = -y * rotationIntensity

    // Update rotation state
    setRotation({ x: rotateX, y: rotateY })

    // Update glare position
    if (glare && glareRef.current) {
      setGlarePosition({ x: x * 100 + 50, y: y * 100 + 50 })
    }
  }

  const handleMouseEnter = () => {
    if (disabled) return
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    if (disabled) return
    setIsHovered(false)
    // Reset rotation and glare
    setRotation({ x: 0, y: 0 })
    setGlarePosition({ x: 50, y: 50 })
  }

  // Handle touch events for mobile
  useEffect(() => {
    const card = containerRef.current
    if (!card || disabled) return

    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0]
        handleMouseMove({
          clientX: touch.clientX,
          clientY: touch.clientY,
        })
      }
    }

    const handleTouchStart = () => {
      handleMouseEnter()
    }

    const handleTouchEnd = () => {
      handleMouseLeave()
    }

    card.addEventListener('touchmove', handleTouchMove)
    card.addEventListener('touchstart', handleTouchStart)
    card.addEventListener('touchend', handleTouchEnd)

    return () => {
      card.removeEventListener('touchmove', handleTouchMove)
      card.removeEventListener('touchstart', handleTouchStart)
      card.removeEventListener('touchend', handleTouchEnd)
    }
  }, [disabled])

  return (
    <div
      ref={containerRef}
      className={cn('relative group', containerClassName)}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        perspective: `${perspective}px`,
        transformStyle: 'preserve-3d',
      }}
      {...props}
    >
      <div
        ref={cardRef}
        className={cn('transition-transform duration-200 ease-out', isHovered ? 'z-10' : 'z-0', cardClassName)}
        style={{
          transform: isHovered
            ? `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) scale3d(${scale}, ${scale}, ${scale})`
            : 'rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
          transformStyle: 'preserve-3d',
        }}
      >
        {children}

        {glare && (
          <div
            ref={glareRef}
            className={cn(
              'absolute inset-0 pointer-events-none opacity-0 transition-opacity duration-300',
              isHovered ? 'opacity-100' : 'opacity-0',
              glareClassName
            )}
            style={{
              background: `radial-gradient(circle at ${glarePosition.x}% ${glarePosition.y}%, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 60%)`,
              transform: 'translateZ(1px)',
            }}
          />
        )}
      </div>
    </div>
  )
}
